/**
 * WHY THE AMENITIES BLOCK LOOKS LIKE IT DISAPPEARED — one line of editor chrome.
 *
 * Layne, 2026-08-28: *"I did notice the amenities block disappeared lol so I'm not
 * sure what that's about."* Nothing broke. `echDyn` blanks the icon slots for a
 * community with fewer than `AMEN_MIN` mappable amenities (Jack, 2026-08-21: "if no
 * amenities leave as blank"), and `amenTrimHtml` then drops every empty cell AND any
 * row left with nothing — so the whole block renders as zero pixels.
 *
 * That behaviour is right: the alternative it replaced was worse, real sends going out
 * with generic icons under the literal word "Amenity". **The silence is the defect.**
 * Measured across all 3,098 communities, the blank case fires for at least 1,351 of
 * them (43.6%) — a third of the network has the literal text "TBD" or "None" in the
 * Airtable Amenities field — so this is not an edge case a requester will meet once.
 *
 * This codebase already refuses to vanish silently everywhere else: a block absent from
 * Content Builder says "Missing from SFMC" on the canvas, an unmatched community says
 * "No live SFMC content … yet" in the panel. The amenity block was the one place
 * something disappeared with no word at all.
 *
 * ── IT CANNOT SHIP, BY CONSTRUCTION ──────────────────────────────────────────────
 * The canvas and the export share the same `render()`, so a note ADDED TO THE BLOCK'S
 * MARKUP would be pasted into Content Builder and mailed to customers (the exact trap
 * documented on `IS_PLACEHOLDER` in email/document.ts). So this returns a STRING, and
 * the caller draws it in the `.esmod` wrapper beside `.mtag` and `.mbar` — editor DOM
 * that `renderRows` never walks. Nothing here touches the email.
 *
 * ── ONE LINE, NO SECOND SENTENCE ─────────────────────────────────────────────────
 * Jack's standing rule for built surfaces is no explanatory prose (2026-08-14, four
 * separate removals in one session). A note that says *why* earns its place; a note
 * that also lectures about the fix does not. So: the community, and what its Airtable
 * row actually says. The panel one click away is where you act on it.
 */
import { AMEN_MIN, amenFromAirtable, type AmenIcon } from "./echAmen";

/** the fields of an `EchPick` this needs — structural, so `EchPick` itself fits */
export interface AmenNotePick {
  name?: string;
  amenities?: string;
  /** set when a masterplan's amenities were unioned from its collections */
  amenitiesFrom?: string[];
}

/** the Airtable options that mean "we have not said", not "there are none" */
const NOT_SAID = (v: string): boolean => {
  const k = v.trim().toLowerCase();
  return k === "none" || k === "tbd";
};

const list = (v: string[]): string =>
  v.length <= 2 ? v.join(" and ") : v.slice(0, 2).join(", ") + " and " + (v.length - 2) + " more";

const quote = (v: string[]): string => list(v.map((s) => '"' + s + '"'));

/**
 * The note for an amenity block that rendered nothing, or `null` when there is
 * nothing to say.
 *
 * @param picked how many icon slots are currently filled on the block
 * @param picks  Step 1's community picks, in order
 * @param lib    the icon library (`/amenity-icons`), so "no icon for these yet" can be
 *               distinguished from "nothing listed" — an empty library means the
 *               folder has not landed, which is a DIFFERENT problem and stays quiet
 */
export function amenEmptyNote(
  picked: number,
  picks: readonly AmenNotePick[] | null | undefined,
  lib: readonly AmenIcon[] | null | undefined,
): string | null {
  if (picked > 0) return null;                 /* it renders — nothing to explain */

  const ps = picks || [];
  const icons = (lib || []) as AmenIcon[];

  /* THE LIBRARY IS THE PRECONDITION FOR EVERY OTHER ANSWER. Before /amenity-icons
     lands, `amenFromAirtable` returns [] for a community with a dozen amenities, and
     a note built on that would blame Airtable for a fetch that hasn't finished. */
  if (!icons.length) return "Amenities hidden — the icon library hasn’t loaded yet";

  /* Amenities are per-community. A market, division or multi-community send has no
     correct answer, so `echDyn` leaves the block's own defaults alone — and those
     defaults ship EMPTY on Layne's merged block, which is why it still looks deleted. */
  if (ps.length !== 1) {
    return ps.length === 0
      ? "Amenities hidden — no community picked yet, so there are none to fill"
      : "Amenities hidden — amenities are per-community and this request has " + ps.length;
  }

  const p = ps[0] || {};
  const who = String(p.name || "").trim() || "This community";
  const raw = String(p.amenities || "").trim();
  const from = (p.amenitiesFrom || []).filter(Boolean);
  /* a masterplan's amenities are borrowed from its collections — say whose, or the
     note points at an Airtable row that legitimately has no Amenities field at all */
  const src = from.length ? " (via " + list(from) + ")" : "";

  if (!raw) return "Amenities hidden — " + who + " has no amenities in Airtable" + src;

  const vals = raw.split("|").map((s) => s.trim()).filter(Boolean);
  const said = vals.filter((v) => !NOT_SAID(v));
  if (!said.length) return "Amenities hidden — " + who + " lists " + quote(vals) + " in Airtable" + src;

  const mapped = amenFromAirtable(raw, icons);
  if (!mapped.length) {
    return "Amenities hidden — none of " + who + "’s amenities have an icon yet: " + list(said) + src;
  }
  /* one icon in a row built as a grid reads as a mistake, so AMEN_MIN blanks it —
     naming the single survivor is what turns "it vanished" into "add one more" */
  return (
    "Amenities hidden — " + who + " maps to only " + mapped.length + " icon" +
    (mapped.length === 1 ? "" : "s") + " (" + list(mapped) + "), and the grid needs " + AMEN_MIN + src
  );
}
